/**
 * IndexedDB wrapper con idb.
 * Stores:
 *   - items: GtdItem por id
 *   - images: Blob por imageId (+ thumbs como imageId + '_thumb')
 *   - meta:  config de sync, onboarding, review state
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { SyncRecord, SyncConfig, ReviewState } from './types';

const DB_NAME = 'kairos_gtd_v1';
const DB_VERSION = 1;

interface KairosDB extends DBSchema {
  items: {
    key: string;
    value: SyncRecord;
    indexes: { 'by-category': string; 'by-updated': number };
  };
  images: {
    key: string;
    value: Blob;
  };
  meta: {
    key: string;
    value: unknown;
  };
}

let dbInstance: IDBPDatabase<KairosDB> | null = null;

export async function getDB(): Promise<IDBPDatabase<KairosDB>> {
  if (dbInstance) return dbInstance;
  dbInstance = await openDB<KairosDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains('items')) {
        const store = db.createObjectStore('items', { keyPath: 'id' });
        store.createIndex('by-category', 'category');
        store.createIndex('by-updated', 'updatedAt');
      }
      if (!db.objectStoreNames.contains('images')) {
        db.createObjectStore('images');
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta');
      }
    }
  });
  return dbInstance;
}

// ---- Items (registros sincronizados) ----

/** Escritura local: queda marcada como sucia para el próximo push. */
export async function putLocal(rec: SyncRecord): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(['items', 'meta'], 'readwrite');
  await tx.objectStore('items').put(rec);
  const dirty = ((await tx.objectStore('meta').get('dirtyIds')) as string[] | undefined) ?? [];
  if (!dirty.includes(rec.id)) dirty.push(rec.id);
  await tx.objectStore('meta').put(dirty, 'dirtyIds');
  await tx.done;
}

/** Escritura que viene del servidor: no se marca sucia. */
export async function putRemote(rec: SyncRecord): Promise<void> {
  const db = await getDB();
  await db.put('items', rec);
}

/**
 * LWW atómico: en una sola transacción lee el local y escribe el remoto solo si no hay
 * local o si `local.updatedAt <= rec.updatedAt`. No marca sucio. Devuelve si escribió.
 */
export async function putRemoteIfNewer(rec: SyncRecord): Promise<boolean> {
  const db = await getDB();
  const tx = db.transaction('items', 'readwrite');
  const cur = await tx.store.get(rec.id);
  const write = !cur || cur.updatedAt <= rec.updatedAt;
  if (write) await tx.store.put(rec);
  await tx.done;
  return write;
}

export async function getRecord(id: string): Promise<SyncRecord | undefined> {
  const db = await getDB();
  return db.get('items', id);
}

export async function getAllRecords(): Promise<SyncRecord[]> {
  const db = await getDB();
  return db.getAll('items');
}

export async function getDirtyIds(): Promise<string[]> {
  return (await getMeta<string[]>('dirtyIds')) ?? [];
}

/** Quita de sucios solo los ids pusheados cuyo updatedAt no cambió durante el sync. */
export async function clearDirty(pushed: { id: string; updatedAt: number }[]): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(['items', 'meta'], 'readwrite');
  const dirty = ((await tx.objectStore('meta').get('dirtyIds')) as string[] | undefined) ?? [];
  const keep: string[] = [];
  for (const id of dirty) {
    const p = pushed.find((x) => x.id === id);
    const cur = (await tx.objectStore('items').get(id)) as SyncRecord | undefined;
    if (!p || (cur && cur.updatedAt !== p.updatedAt)) keep.push(id);
  }
  await tx.objectStore('meta').put(keep, 'dirtyIds');
  await tx.done;
}

export async function markAllDirty(): Promise<void> {
  const db = await getDB();
  const ids = (await db.getAllKeys('items')) as string[];
  await db.put('meta', ids, 'dirtyIds');
}

// ---- Images ----

export async function putImage(id: string, blob: Blob): Promise<void> {
  const db = await getDB();
  await db.put('images', blob, id);
}

export async function getImage(id: string): Promise<Blob | undefined> {
  const db = await getDB();
  return db.get('images', id);
}

export async function deleteImage(id: string): Promise<void> {
  const db = await getDB();
  await db.delete('images', id);
  await db.delete('images', id + '_thumb');
}

export async function getAllImageKeys(): Promise<string[]> {
  const db = await getDB();
  return db.getAllKeys('images') as Promise<string[]>;
}

// ---- Meta ----

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const db = await getDB();
  return db.get('meta', key) as Promise<T | undefined>;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  const db = await getDB();
  await db.put('meta', value, key);
}

export async function getSyncConfig(): Promise<SyncConfig | undefined> {
  return getMeta<SyncConfig>('syncConfig');
}

export async function setSyncConfig(cfg: SyncConfig): Promise<void> {
  return setMeta('syncConfig', cfg);
}

export async function getReviewState(): Promise<ReviewState> {
  return (
    (await getMeta<ReviewState>('review')) ?? {
      checks: [],
      startedAt: null
    }
  );
}

export async function setReviewState(state: ReviewState): Promise<void> {
  return setMeta('review', state);
}

export async function getLastSyncAt(): Promise<number> {
  return (await getMeta<number>('lastSyncAt')) ?? 0;
}

export async function setLastSyncAt(ts: number): Promise<void> {
  return setMeta('lastSyncAt', ts);
}

export async function getAccountKey(): Promise<CryptoKey | undefined> {
  return getMeta<CryptoKey>('accountKey');
}

export async function setAccountKey(key: CryptoKey | null): Promise<void> {
  const db = await getDB();
  if (key) await db.put('meta', key, 'accountKey');
  else await db.delete('meta', 'accountKey');
}

// ---- Utilities ----

export function genId(): string {
  return 'g' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

export async function compressImage(
  blob: Blob,
  maxPx = 1200,
  quality = 0.7
): Promise<Blob> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let w = img.width;
      let h = img.height;
      if (w > maxPx || h > maxPx) {
        const ratio = Math.min(maxPx / w, maxPx / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve(blob);
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob((b) => resolve(b ?? blob), 'image/jpeg', quality);
    };
    img.onerror = () => resolve(blob);
    img.src = URL.createObjectURL(blob);
  });
}
